import React, { useState, useEffect, useCallback } from 'react';
import { Tag, FileText, LayoutList, Zap, UserCog, BarChart3, Shield, BookOpen, DollarSign, CheckCircle, Play, Send, RefreshCw, ArrowDownLeft, AlertTriangle, Clock, Database, HardDrive, Download, Globe, Eye, EyeOff, Link, Unlink, Users, Activity, Copy } from 'lucide-react';
import { safeAsync, neuShadow, neuShadowSm, neuShadowInset, btnPrimary, btnSm, Badge } from './crmShared';

// ─── GHL Sync Section ─────────────────────────────────────────────────────

function GHLSyncSection({ canCreate, loadExtended }) {
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [syncError, setSyncError] = useState(null);
  const [lastSync, setLastSync] = useState(null);
  const [elapsed, setElapsed] = useState(0);

  const loadLastSync = useCallback(async () => {
    try {
      const status = await window.zignagenixAPI.ghlGetSyncStatus();
      if (status && !status.error) setLastSync(status);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadLastSync(); }, [loadLastSync]);

  // Timer while syncing
  useEffect(() => {
    if (!syncing) return;
    const t = setInterval(() => setElapsed(e => e + 1), 1000);
    return () => clearInterval(t);
  }, [syncing]);

  async function runFullSync() {
    setSyncing(true);
    setSyncResult(null);
    setSyncError(null);
    setElapsed(0);
    try {
      const result = await window.zignagenixAPI.ghlSyncFull();
      if (result.success === false) {
        setSyncError(result.error || 'Sync failed');
      } else {
        setSyncResult(result);
        await loadLastSync();
        if (loadExtended) loadExtended();
      }
    } catch (err) {
      setSyncError(err.message || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  }

  const phaseCard = (label, icon, color, data) => {
    if (!data) return null;
    if (data.error) return (
      <div style={{ padding: 10, background: '#FEF2F2', borderRadius: 8, boxShadow: neuShadowSm }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: '#DC3545', display: 'flex', alignItems: 'center', gap: 4 }}>
          <AlertTriangle size={12} /> {label}: {data.error}
        </div>
      </div>
    );
    const items = Object.entries(data).filter(([k]) => k !== 'total' && k !== 'error');
    return (
      <div style={{ padding: 10, background: '#F8F9FA', borderRadius: 8, boxShadow: neuShadowSm }}>
        <div style={{ fontSize: 11, fontWeight: 700, color, display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
          {icon} {label}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {items.map(([k, v]) => (
            <span key={k} style={{ fontSize: 11, color: k === 'skippedLocalEdits' ? '#B8860B' : '#6C757D' }}>
              <strong style={{ color: '#212529' }}>{v}</strong> {k === 'skippedLocalEdits' ? 'skipped (locally edited)' : k}
            </span>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div style={{ marginTop: 20, background: '#F8F9FA', borderRadius: 14, border: 'none', boxShadow: neuShadow, padding: 20, borderLeft: '4px solid #F26522' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
        <div>
          <h4 style={{ fontSize: 14, fontWeight: 700, color: '#212529', margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: 8 }}>
            <ArrowDownLeft size={16} color="#F26522" /> GoHighLevel Sync
          </h4>
          <p style={{ fontSize: 11, color: '#6C757D', margin: 0 }}>
            GoHighLevel → CRM (one-way): contacts, conversations, tasks & opportunities. Runs nightly at midnight ET.
          </p>
        </div>
        {lastSync?.syncedAt && (
          <div style={{ textAlign: 'right', flexShrink: 0 }}>
            <div style={{ fontSize: 10, color: '#ADB5BD', display: 'flex', alignItems: 'center', gap: 4 }}>
              <Clock size={10} /> Last sync
            </div>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#6C757D' }}>
              {new Date(lastSync.syncedAt).toLocaleDateString()} {new Date(lastSync.syncedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </div>
          </div>
        )}
      </div>

      {/* Sync Button */}
      <div style={{ marginBottom: 14 }}>
        {canCreate ? (
          <button onClick={runFullSync} disabled={syncing} style={{
            ...btnPrimary, background: syncing ? '#ADB5BD' : 'linear-gradient(135deg, #F26522, #E55A1B)',
            padding: '10px 24px', fontSize: 13, width: '100%', justifyContent: 'center',
          }}>
            {syncing ? (
              <>
                <RefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }} />
                Syncing with GoHighLevel... ({elapsed}s)
              </>
            ) : (
              <><ArrowDownLeft size={14} /> Sync Now — Full Import from GoHighLevel</>
            )}
          </button>
        ) : (
          <div style={{ fontSize: 12, color: '#ADB5BD', textAlign: 'center', padding: 10 }}>
            You need CRM create permission to run a sync.
          </div>
        )}
      </div>

      {/* Sync Error */}
      {syncError && (
        <div style={{ padding: 12, background: '#FEF2F2', borderRadius: 10, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
          <AlertTriangle size={14} color="#DC3545" />
          <span style={{ fontSize: 12, color: '#DC3545', fontWeight: 600 }}>{syncError}</span>
        </div>
      )}

      {/* Sync Results */}
      {syncResult && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
            <CheckCircle size={14} color="#62BB46" />
            <span style={{ fontSize: 12, fontWeight: 700, color: '#62BB46' }}>Sync completed successfully</span>
          </div>
          {phaseCard('Contacts', <RefreshCw size={11} />, '#2E8B98', syncResult.contacts)}
          {phaseCard('Conversations', <Send size={11} />, '#62BB46', syncResult.conversations)}
          {phaseCard('Tasks', <CheckCircle size={11} />, '#F26522', syncResult.tasks)}
          {phaseCard('Opportunities', <DollarSign size={11} />, '#256F7A', syncResult.opportunities)}
        </div>
      )}
    </div>
  );
}

// ─── Database Backup Section ─────────────────────────────────────────────

function BackupSection({ canCreate }) {
  const [backing, setBacking] = useState(false);
  const [backupType, setBackupType] = useState('full');
  const [backupResult, setBackupResult] = useState(null);
  const [backupError, setBackupError] = useState(null);
  const [backupStatus, setBackupStatus] = useState(null);
  const [elapsed, setElapsed] = useState(0);

  const loadStatus = useCallback(async () => {
    try {
      const st = await window.zignagenixAPI.backupStatus();
      if (st && !st.error) setBackupStatus(st);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  useEffect(() => {
    if (!backing) return;
    const t = setInterval(() => setElapsed(e => e + 1), 1000);
    return () => clearInterval(t);
  }, [backing]);

  async function runBackup() {
    setBacking(true);
    setBackupResult(null);
    setBackupError(null);
    setElapsed(0);
    try {
      const result = await window.zignagenixAPI.backupRunNow({ type: backupType });
      if (result.error) {
        setBackupError(result.error);
      } else {
        setBackupResult(result);
        await loadStatus();
      }
    } catch (err) {
      setBackupError(err.message || 'Backup failed');
    } finally {
      setBacking(false);
    }
  }

  const formatBytes = (bytes) => {
    if (!bytes || bytes === 0) return '0 B';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1024 / 1024).toFixed(2) + ' MB';
  };

  const lastBackup = backupStatus?.lastBackup;
  const stats = backupStatus?.stats;

  return (
    <div style={{ marginTop: 20, background: '#F8F9FA', borderRadius: 14, border: 'none', boxShadow: neuShadow, padding: 20, borderLeft: '4px solid #256F7A' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
        <div>
          <h4 style={{ fontSize: 14, fontWeight: 700, color: '#212529', margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Database size={16} color="#256F7A" /> Database Backup
          </h4>
          <p style={{ fontSize: 11, color: '#6C757D', margin: 0 }}>
            HIPAA-compliant backups to AWS S3. Full backups run nightly at 2 AM ET. 6-year retention.
          </p>
        </div>
        {lastBackup && (
          <div style={{ textAlign: 'right', flexShrink: 0 }}>
            <div style={{ fontSize: 10, color: '#ADB5BD', display: 'flex', alignItems: 'center', gap: 4 }}>
              <Clock size={10} /> Last backup
            </div>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#6C757D' }}>
              {new Date(lastBackup.created_at).toLocaleDateString()} {new Date(lastBackup.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </div>
          </div>
        )}
      </div>

      {/* Backup Type Selector + Button */}
      <div style={{ marginBottom: 14, display: 'flex', gap: 8 }}>
        <select
          value={backupType}
          onChange={(e) => setBackupType(e.target.value)}
          disabled={backing}
          style={{
            flex: '0 0 auto', padding: '8px 12px', fontSize: 12, borderRadius: 8,
            border: '1px solid #DEE2E6', background: '#fff', color: '#212529',
            cursor: 'pointer', fontWeight: 600,
          }}
        >
          <option value="full">Full Backup (pg_dump)</option>
          <option value="incremental">Incremental (changed rows)</option>
        </select>
        {canCreate ? (
          <button onClick={runBackup} disabled={backing} style={{
            ...btnPrimary, background: backing ? '#ADB5BD' : 'linear-gradient(135deg, #256F7A, #2E8B98)',
            padding: '10px 24px', fontSize: 13, flex: 1, justifyContent: 'center',
          }}>
            {backing ? (
              <>
                <RefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }} />
                Backing up... ({elapsed}s)
              </>
            ) : (
              <><HardDrive size={14} /> Backup Now</>
            )}
          </button>
        ) : (
          <div style={{ fontSize: 12, color: '#ADB5BD', textAlign: 'center', padding: 10, flex: 1 }}>
            You need settings permission to run a backup.
          </div>
        )}
      </div>

      {/* Error */}
      {backupError && (
        <div style={{ padding: 12, background: '#FEF2F2', borderRadius: 10, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
          <AlertTriangle size={14} color="#DC3545" />
          <span style={{ fontSize: 12, color: '#DC3545', fontWeight: 600 }}>{backupError}</span>
        </div>
      )}

      {/* Backup Result */}
      {backupResult && (
        <div style={{ padding: 12, background: '#F0FDF4', borderRadius: 10, marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
            <CheckCircle size={14} color="#62BB46" />
            <span style={{ fontSize: 12, fontWeight: 700, color: '#62BB46' }}>
              {backupResult.skipped ? 'No changes since last backup' : 'Backup completed successfully'}
            </span>
          </div>
          {!backupResult.skipped && (
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 11, color: '#6C757D' }}>
                <strong style={{ color: '#212529' }}>{backupResult.type}</strong> type
              </span>
              <span style={{ fontSize: 11, color: '#6C757D' }}>
                <strong style={{ color: '#212529' }}>{backupResult.sizeMB} MB</strong> size
              </span>
              <span style={{ fontSize: 11, color: '#6C757D' }}>
                <strong style={{ color: '#212529' }}>{(backupResult.durationMs / 1000).toFixed(1)}s</strong> duration
              </span>
              <span style={{ fontSize: 11, color: '#6C757D' }}>
                <strong style={{ color: '#212529' }}>{backupResult.rowCount?.toLocaleString()}</strong> rows
              </span>
            </div>
          )}
        </div>
      )}

      {/* Stats Summary */}
      {stats && (parseInt(stats.total_completed) > 0 || parseInt(stats.total_failed) > 0) && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: 8 }}>
          <div style={{ padding: 8, background: '#F0FDF4', borderRadius: 8, boxShadow: neuShadowSm, textAlign: 'center' }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#62BB46' }}>{stats.total_completed || 0}</div>
            <div style={{ fontSize: 10, color: '#6C757D' }}>Successful</div>
          </div>
          {parseInt(stats.total_failed) > 0 && (
            <div style={{ padding: 8, background: '#FEF2F2', borderRadius: 8, boxShadow: neuShadowSm, textAlign: 'center' }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#DC3545' }}>{stats.total_failed}</div>
              <div style={{ fontSize: 10, color: '#6C757D' }}>Failed</div>
            </div>
          )}
          <div style={{ padding: 8, background: '#F8F9FA', borderRadius: 8, boxShadow: neuShadowSm, textAlign: 'center' }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#212529' }}>{formatBytes(parseInt(stats.total_bytes || 0))}</div>
            <div style={{ fontSize: 10, color: '#6C757D' }}>Total Size</div>
          </div>
        </div>
      )}

      {/* Recent History */}
      {backupStatus?.history?.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#6C757D', marginBottom: 6 }}>Recent Backups</div>
          <div style={{ maxHeight: 140, overflowY: 'auto' }}>
            {backupStatus.history.slice(0, 5).map(b => (
              <div key={b.id} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '6px 0', borderBottom: '1px solid #F0F0F0', fontSize: 11,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  {b.status === 'completed' ? <CheckCircle size={10} color="#62BB46" /> : <AlertTriangle size={10} color="#DC3545" />}
                  <span style={{ fontWeight: 600, color: '#212529' }}>{b.backup_type}</span>
                  <span style={{ color: '#ADB5BD' }}>{formatBytes(b.file_size_bytes)}</span>
                </div>
                <span style={{ color: '#ADB5BD' }}>
                  {new Date(b.created_at).toLocaleDateString()} {new Date(b.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Meta / Facebook Configuration Section ───────────────────────────────

function MetaConfigSection({ canCreate }) {
  const [config, setConfig] = useState({ app_id: '', app_secret: '', access_token: '', pixel_id: '', dataset_id: '', webhook_verify_token: '', ad_account_id: '', business_id: '' });
  const [savedConfig, setSavedConfig] = useState(null);
  const [showToken, setShowToken] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Pages & subscriptions
  const [pages, setPages] = useState([]);
  const [loadingPages, setLoadingPages] = useState(false);
  const [subscribingPage, setSubscribingPage] = useState(null);

  // Lead stats
  const [stats, setStats] = useState(null);

  // Schema init
  const [schemaReady, setSchemaReady] = useState(false);

  // Sync log
  const [syncLog, setSyncLog] = useState([]);

  // Webhook URL
  const webhookUrl = typeof window !== 'undefined' ? `${window.location.origin}/webhooks/meta` : '/webhooks/meta';

  const loadConfig = useCallback(async () => {
    try {
      const res = await window.zignagenixAPI.metaGetConfig();
      if (res.success && res.config) {
        setSavedConfig(res.config);
        setConfig(prev => ({
          ...prev,
          app_id: res.config.app_id || '',
          pixel_id: res.config.pixel_id || '',
          dataset_id: res.config.dataset_id || '',
          webhook_verify_token: res.config.webhook_verify_token || '',
          ad_account_id: res.config.ad_account_id || '',
          business_id: res.config.business_id || '',
        }));
        setSchemaReady(true);
      }
    } catch { /* not configured yet */ }
  }, []);

  const loadStats = useCallback(async () => {
    try {
      const res = await window.zignagenixAPI.metaGetLeadStats();
      if (res) setStats(res);
    } catch { /* tables may not exist */ }
  }, []);

  const loadSyncLog = useCallback(async () => {
    try {
      const res = await window.zignagenixAPI.metaGetSyncLog();
      if (res?.log) setSyncLog(res.log.slice(0, 8));
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadConfig(); loadStats(); loadSyncLog(); }, [loadConfig, loadStats, loadSyncLog]);

  async function ensureSchema() {
    try {
      const res = await window.zignagenixAPI.metaEnsureSchema();
      if (res.success) { setSchemaReady(true); setSuccess('Meta database tables created.'); }
      else setError(res.error || 'Failed to create schema');
    } catch (err) { setError(err.message); }
  }

  async function saveConfig() {
    setSaving(true); setError(null); setSuccess(null);
    try {
      if (!schemaReady) await ensureSchema();
      const payload = { ...config };
      // Don't send empty strings for optional fields
      Object.keys(payload).forEach(k => { if (!payload[k]) delete payload[k]; });
      if (!payload.access_token && savedConfig?.access_token) {
        // Keep existing token if user hasn't entered a new one
        delete payload.access_token;
      }
      const res = await window.zignagenixAPI.metaSaveConfig(payload);
      if (res.success) {
        setSuccess('Meta configuration saved successfully.');
        await loadConfig();
      } else {
        setError(res.error || 'Failed to save');
      }
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  }

  async function testConnection() {
    setTesting(true); setTestResult(null); setError(null);
    try {
      const res = await window.zignagenixAPI.metaTestConnection();
      setTestResult(res);
      if (res.success) {
        setSuccess('Connection verified — token is valid.');
      } else {
        setError(res.error || 'Connection test failed');
      }
    } catch (err) { setError(err.message); }
    finally { setTesting(false); }
  }

  async function loadPages() {
    setLoadingPages(true);
    try {
      const res = await window.zignagenixAPI.metaListPages();
      if (res.success) setPages(res.pages || []);
      else setError(res.error || 'Could not load pages');
    } catch (err) { setError(err.message); }
    finally { setLoadingPages(false); }
  }

  async function subscribePage(pageId) {
    setSubscribingPage(pageId);
    try {
      const res = await window.zignagenixAPI.metaSubscribePage(pageId);
      if (res.success) {
        setSuccess(`Page subscribed to lead webhooks.`);
        await loadPages();
        await loadStats();
      } else { setError(res.error); }
    } catch (err) { setError(err.message); }
    finally { setSubscribingPage(null); }
  }

  async function unsubscribePage(pageId) {
    setSubscribingPage(pageId);
    try {
      const res = await window.zignagenixAPI.metaUnsubscribePage(pageId);
      if (res.success) {
        setSuccess('Page unsubscribed.');
        await loadStats();
      } else { setError(res.error); }
    } catch (err) { setError(err.message); }
    finally { setSubscribingPage(null); }
  }

  function copyWebhookUrl() {
    navigator.clipboard?.writeText(webhookUrl);
    setSuccess('Webhook URL copied to clipboard.');
    setTimeout(() => setSuccess(null), 2000);
  }

  const inputStyle = {
    width: '100%', padding: '8px 10px', fontSize: 12, borderRadius: 8,
    border: '1px solid #DEE2E6', background: '#fff', color: '#212529',
    fontFamily: 'monospace', boxSizing: 'border-box',
  };

  const labelStyle = { fontSize: 11, fontWeight: 700, color: '#495057', marginBottom: 3, display: 'block' };

  const isConfigured = !!savedConfig?.access_token;

  return (
    <div style={{ marginTop: 20, background: '#F8F9FA', borderRadius: 14, border: 'none', boxShadow: neuShadow, padding: 20, borderLeft: '4px solid #1877F2' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
        <div>
          <h4 style={{ fontSize: 14, fontWeight: 700, color: '#212529', margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Globe size={16} color="#1877F2" /> Meta / Facebook Integration
          </h4>
          <p style={{ fontSize: 11, color: '#6C757D', margin: 0 }}>
            Lead Ads, Conversions API, Custom Audiences. Connect your Facebook pages for real-time lead capture.
          </p>
        </div>
        {isConfigured && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
            <div style={{ width: 8, height: 8, borderRadius: 4, background: '#62BB46' }} />
            <span style={{ fontSize: 11, fontWeight: 600, color: '#62BB46' }}>Connected</span>
          </div>
        )}
      </div>

      {/* Success / Error */}
      {success && (
        <div style={{ padding: 10, background: '#F0FDF4', borderRadius: 8, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
          <CheckCircle size={12} color="#62BB46" />
          <span style={{ fontSize: 11, color: '#2D6A4F', fontWeight: 600 }}>{success}</span>
        </div>
      )}
      {error && (
        <div style={{ padding: 10, background: '#FEF2F2', borderRadius: 8, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
          <AlertTriangle size={12} color="#DC3545" />
          <span style={{ fontSize: 11, color: '#DC3545', fontWeight: 600 }}>{error}</span>
        </div>
      )}

      {/* ─── Credentials Form ───────────────────────────────────────────── */}
      {canCreate && (
        <div style={{ background: '#fff', borderRadius: 10, padding: 14, marginBottom: 14, boxShadow: neuShadowSm }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#212529', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
            <Shield size={13} color="#1877F2" /> API Credentials
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div>
              <label style={labelStyle}>App ID</label>
              <input style={inputStyle} placeholder="Meta App ID" value={config.app_id}
                onChange={e => setConfig(p => ({ ...p, app_id: e.target.value }))} />
            </div>
            <div>
              <label style={labelStyle}>App Secret</label>
              <input style={inputStyle} type="password" placeholder="For webhook signature verification"
                value={config.app_secret} onChange={e => setConfig(p => ({ ...p, app_secret: e.target.value }))} />
            </div>
          </div>

          <div style={{ marginTop: 10 }}>
            <label style={labelStyle}>Access Token {savedConfig?.access_token && <span style={{ color: '#ADB5BD', fontWeight: 400 }}>(currently set: {savedConfig.access_token})</span>}</label>
            <div style={{ position: 'relative' }}>
              <input style={{ ...inputStyle, paddingRight: 36 }}
                type={showToken ? 'text' : 'password'} placeholder="System User or Page Access Token"
                value={config.access_token}
                onChange={e => setConfig(p => ({ ...p, access_token: e.target.value }))} />
              <button onClick={() => setShowToken(!showToken)} style={{
                position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
                background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: '#ADB5BD',
              }}>
                {showToken ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 10 }}>
            <div>
              <label style={labelStyle}>Pixel ID</label>
              <input style={inputStyle} placeholder="Facebook Pixel ID (for CAPI)" value={config.pixel_id}
                onChange={e => setConfig(p => ({ ...p, pixel_id: e.target.value }))} />
            </div>
            <div>
              <label style={labelStyle}>Ad Account ID</label>
              <input style={inputStyle} placeholder="act_123456789 (for audiences)" value={config.ad_account_id}
                onChange={e => setConfig(p => ({ ...p, ad_account_id: e.target.value }))} />
            </div>
          </div>

          {/* Save + Test buttons */}
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button onClick={saveConfig} disabled={saving} style={{
              ...btnPrimary, background: saving ? '#ADB5BD' : 'linear-gradient(135deg, #1877F2, #0C5DC7)',
              padding: '8px 20px', fontSize: 12, flex: 1, justifyContent: 'center',
            }}>
              {saving ? <><RefreshCw size={12} style={{ animation: 'spin 1s linear infinite' }} /> Saving...</> : <><Shield size={12} /> Save Configuration</>}
            </button>
            <button onClick={testConnection} disabled={testing || !isConfigured} style={{
              ...btnSm, color: '#1877F2', borderColor: '#1877F240', padding: '8px 16px', fontSize: 12,
              opacity: isConfigured ? 1 : 0.5,
            }}>
              {testing ? <><RefreshCw size={12} style={{ animation: 'spin 1s linear infinite' }} /> Testing...</> : <><Activity size={12} /> Test Connection</>}
            </button>
          </div>

          {/* Test result */}
          {testResult?.success && testResult.permissions && (
            <div style={{ marginTop: 10, padding: 10, background: '#F0FDF4', borderRadius: 8, fontSize: 11 }}>
              <div style={{ fontWeight: 700, color: '#2D6A4F', marginBottom: 4 }}>
                Token valid — {testResult.app_name || 'Meta App'} ({testResult.token_type || 'token'})
              </div>
              <div style={{ color: '#6C757D', display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {(testResult.permissions || []).map(p => (
                  <span key={p} style={{ background: '#E8F5E9', padding: '2px 6px', borderRadius: 4, fontSize: 10 }}>{p}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ─── Webhook URL ────────────────────────────────────────────────── */}
      {isConfigured && (
        <div style={{ background: '#fff', borderRadius: 10, padding: 14, marginBottom: 14, boxShadow: neuShadowSm }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#212529', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
            <Link size={13} color="#1877F2" /> Webhook Configuration
          </div>
          <p style={{ fontSize: 11, color: '#6C757D', margin: '0 0 8px' }}>
            Add this URL in your Meta Developer dashboard under Webhooks &rarr; leadgen.
          </p>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <code style={{
              flex: 1, padding: '8px 10px', background: '#F4F4F4', borderRadius: 6, fontSize: 11,
              fontFamily: 'monospace', color: '#212529', border: '1px solid #DEE2E6', wordBreak: 'break-all',
            }}>{webhookUrl}</code>
            <button onClick={copyWebhookUrl} style={{ ...btnSm, color: '#1877F2', borderColor: '#1877F240', padding: '6px 10px' }}>
              <Copy size={12} />
            </button>
          </div>
          {savedConfig?.webhook_verify_token && (
            <div style={{ marginTop: 8 }}>
              <span style={{ fontSize: 10, color: '#ADB5BD' }}>Verify Token: </span>
              <code style={{ fontSize: 10, color: '#6C757D', fontFamily: 'monospace' }}>{savedConfig.webhook_verify_token}</code>
            </div>
          )}
        </div>
      )}

      {/* ─── Facebook Pages & Subscriptions ─────────────────────────────── */}
      {isConfigured && (
        <div style={{ background: '#fff', borderRadius: 10, padding: 14, marginBottom: 14, boxShadow: neuShadowSm }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#212529', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Globe size={13} color="#1877F2" /> Facebook Pages
            </div>
            <button onClick={loadPages} disabled={loadingPages} style={{
              ...btnSm, color: '#1877F2', borderColor: '#1877F240', padding: '4px 10px', fontSize: 11,
            }}>
              {loadingPages ? <><RefreshCw size={10} style={{ animation: 'spin 1s linear infinite' }} /> Loading...</> : <><RefreshCw size={10} /> Load Pages</>}
            </button>
          </div>

          {/* Active subscriptions from stats */}
          {stats?.page_subscriptions?.length > 0 && (
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: '#6C757D', marginBottom: 4 }}>Active Subscriptions</div>
              {stats.page_subscriptions.filter(s => s.is_active).map(s => (
                <div key={s.page_id} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '6px 8px', background: '#F0FDF4', borderRadius: 6, marginBottom: 4, fontSize: 11,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <CheckCircle size={10} color="#62BB46" />
                    <span style={{ fontWeight: 600 }}>{s.page_name || s.page_id}</span>
                  </div>
                  <button onClick={() => unsubscribePage(s.page_id)}
                    disabled={subscribingPage === s.page_id}
                    style={{ ...btnSm, color: '#DC3545', borderColor: '#DC354540', padding: '2px 8px', fontSize: 10 }}>
                    <Unlink size={9} /> Unsubscribe
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Available pages */}
          {pages.length > 0 && (
            <div>
              <div style={{ fontSize: 10, fontWeight: 600, color: '#6C757D', marginBottom: 4 }}>Available Pages</div>
              {pages.map(p => {
                const isSubscribed = stats?.page_subscriptions?.some(s => s.page_id === p.id && s.is_active);
                return (
                  <div key={p.id} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '6px 8px', background: '#F8F9FA', borderRadius: 6, marginBottom: 4, fontSize: 11,
                  }}>
                    <div>
                      <span style={{ fontWeight: 600, color: '#212529' }}>{p.name}</span>
                      <span style={{ color: '#ADB5BD', marginLeft: 6, fontSize: 10 }}>{p.category} · {(p.fan_count || 0).toLocaleString()} fans</span>
                    </div>
                    {isSubscribed ? (
                      <span style={{ fontSize: 10, color: '#62BB46', fontWeight: 600 }}>Subscribed</span>
                    ) : (
                      <button onClick={() => subscribePage(p.id)}
                        disabled={subscribingPage === p.id}
                        style={{ ...btnSm, color: '#1877F2', borderColor: '#1877F240', padding: '2px 8px', fontSize: 10 }}>
                        {subscribingPage === p.id ? <RefreshCw size={9} style={{ animation: 'spin 1s linear infinite' }} /> : <><Link size={9} /> Subscribe</>}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {pages.length === 0 && !loadingPages && (
            <p style={{ fontSize: 11, color: '#ADB5BD', margin: 0 }}>Click "Load Pages" to see Facebook pages accessible by your token.</p>
          )}
        </div>
      )}

      {/* ─── Lead Stats Dashboard ───────────────────────────────────────── */}
      {isConfigured && stats && (
        <div style={{ background: '#fff', borderRadius: 10, padding: 14, marginBottom: 14, boxShadow: neuShadowSm }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#212529', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
            <Users size={13} color="#1877F2" /> Lead Stats
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))', gap: 8, marginBottom: 10 }}>
            <div style={{ padding: 8, background: '#EFF6FF', borderRadius: 8, textAlign: 'center', boxShadow: neuShadowSm }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: '#1877F2' }}>{stats.total_leads || 0}</div>
              <div style={{ fontSize: 9, color: '#6C757D' }}>Total Leads</div>
            </div>
            <div style={{ padding: 8, background: '#F0FDF4', borderRadius: 8, textAlign: 'center', boxShadow: neuShadowSm }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: '#62BB46' }}>{stats.by_status?.processed || 0}</div>
              <div style={{ fontSize: 9, color: '#6C757D' }}>Processed</div>
            </div>
            <div style={{ padding: 8, background: '#FEF9C3', borderRadius: 8, textAlign: 'center', boxShadow: neuShadowSm }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: '#D97706' }}>{stats.by_status?.pending || 0}</div>
              <div style={{ fontSize: 9, color: '#6C757D' }}>Pending</div>
            </div>
            <div style={{ padding: 8, background: '#FEF2F2', borderRadius: 8, textAlign: 'center', boxShadow: neuShadowSm }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: '#DC3545' }}>{stats.by_status?.failed || 0}</div>
              <div style={{ fontSize: 9, color: '#6C757D' }}>Failed</div>
            </div>
            <div style={{ padding: 8, background: '#F3E8FF', borderRadius: 8, textAlign: 'center', boxShadow: neuShadowSm }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: '#7C3AED' }}>{stats.capi_events_total || 0}</div>
              <div style={{ fontSize: 9, color: '#6C757D' }}>CAPI Events</div>
            </div>
          </div>

          {/* Platform breakdown */}
          {stats.by_platform && Object.keys(stats.by_platform).length > 0 && (
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              {Object.entries(stats.by_platform).map(([platform, count]) => (
                <span key={platform} style={{ fontSize: 10, background: '#F0F4FF', padding: '3px 8px', borderRadius: 4, color: '#1877F2', fontWeight: 600 }}>
                  {platform}: {count}
                </span>
              ))}
            </div>
          )}

          {/* Top campaigns */}
          {stats.top_campaigns?.length > 0 && (
            <div>
              <div style={{ fontSize: 10, fontWeight: 600, color: '#6C757D', marginBottom: 4 }}>Top Campaigns</div>
              {stats.top_campaigns.slice(0, 5).map((c, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: 11, borderBottom: '1px solid #F0F0F0' }}>
                  <span style={{ color: '#212529' }}>{c.campaign}</span>
                  <span style={{ fontWeight: 700, color: '#1877F2' }}>{c.count}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ─── Recent Sync Log ────────────────────────────────────────────── */}
      {isConfigured && syncLog.length > 0 && (
        <div style={{ background: '#fff', borderRadius: 10, padding: 14, boxShadow: neuShadowSm }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#212529', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
            <Activity size={13} color="#1877F2" /> Recent Activity
          </div>
          <div style={{ maxHeight: 140, overflowY: 'auto' }}>
            {syncLog.map((entry, i) => (
              <div key={i} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '5px 0', borderBottom: '1px solid #F0F0F0', fontSize: 11,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  {entry.status === 'success' ? <CheckCircle size={10} color="#62BB46" /> : <AlertTriangle size={10} color="#DC3545" />}
                  <span style={{ fontWeight: 600, color: '#212529' }}>{entry.action || entry.entity_type}</span>
                  <span style={{ color: '#ADB5BD' }}>{entry.direction}</span>
                </div>
                <span style={{ color: '#ADB5BD', fontSize: 10 }}>
                  {entry.created_at ? new Date(entry.created_at).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ─── Initial Setup (no config) ──────────────────────────────────── */}
      {!isConfigured && !canCreate && (
        <div style={{ fontSize: 12, color: '#ADB5BD', textAlign: 'center', padding: 20 }}>
          Meta integration is not configured. An admin needs to add API credentials.
        </div>
      )}
    </div>
  );
}

export default function SetupTab({
  tags, customFields, pipelines, workflows, staff, scorecardMetrics,
  escalations, pipelineRules, compPlans, templates, workflowRuns,
  canCreate, seeding, setSeeding, seedAll, loadExtended
}) {
  const setupSteps = [
    { key: 'tags', label: 'Tags', expected: 12, actual: tags.length, icon: Tag, color: '#2E8B98',
      desc: '12 patient-journey tags (NEW-LEAD, CONTACTED, QUALIFIED, CONSULT-BOOKED, etc.)',
      seed: async () => { await window.zignagenixAPI.seedDefaultTags(); await loadExtended(); } },
    { key: 'fields', label: 'Custom Fields', expected: 11, actual: customFields.length, icon: FileText, color: '#F26522',
      desc: '11 fields (Lead Source, Condition, Budget Range, Consult Date, Quote Amount, etc.)',
      seed: async () => { await window.zignagenixAPI.seedDefaultCustomFields(); await loadExtended(); } },
    { key: 'pipeline', label: 'Pipeline Stages', expected: 14, actual: pipelines.reduce((a, p) => a + (p.stages?.length || 0), 0), icon: LayoutList, color: '#2E8B98',
      desc: '14-stage patient journey pipeline (New Lead → Therapy Completed)',
      seed: async () => { await window.zignagenixAPI.seedDefaultPipeline(); await loadExtended(); } },
    { key: 'workflows', label: 'Workflows', expected: 12, actual: workflows.length, icon: Zap, color: '#256F7A',
      desc: '12 automated workflows (WF1 Speed-to-Contact through WF12 Post-Therapy Follow-Up)',
      seed: async () => { await window.zignagenixAPI.seedAllWorkflows(); await loadExtended(); } },
    { key: 'staff', label: 'CRM Team Roles', expected: 5, actual: staff.length, icon: UserCog, color: '#2E8B98',
      desc: '5 CRM roles: Lead Response, Patient Experience, Closer, Case Coordinator, Manager',
      seed: async () => { await window.zignagenixAPI.seedDefaultStaff(); await loadExtended(); } },
    { key: 'scorecard', label: 'Scorecard Metrics', expected: 17, actual: scorecardMetrics.length, icon: BarChart3, color: '#62BB46',
      desc: '17 performance metrics across all roles (response time, contact rate, close rate, etc.)',
      seed: async () => { await window.zignagenixAPI.seedDefaultScorecard(); await loadExtended(); } },
    { key: 'escalations', label: 'Escalation Rules', expected: 4, actual: escalations.length, icon: Shield, color: '#DC3545',
      desc: '4 rules: 10-min uncontacted, 72hr no consult, 15-min protocol, 7-day no deposit',
      seed: async () => { await window.zignagenixAPI.seedDefaultEscalations(); await loadExtended(); } },
    { key: 'rules', label: 'Pipeline Rules', expected: 8, actual: pipelineRules.length, icon: BookOpen, color: '#2E8B98',
      desc: '8 rules: No skipping stages, one owner per stage, required fields, etc.',
      seed: async () => { await window.zignagenixAPI.seedDefaultPipelineRules(); await loadExtended(); } },
    { key: 'comp', label: 'Compensation Plans', expected: 3, actual: compPlans.length, icon: DollarSign, color: '#4FA038',
      desc: '3 plans: Lead Response (1%), PEM (2%), Closer (4%) — 7% total cap',
      seed: async () => { await window.zignagenixAPI.seedDefaultComp(); await loadExtended(); } },
  ];
  const completedSteps = setupSteps.filter(s => s.actual >= s.expected).length;
  const allDone = completedSteps === setupSteps.length;
  const pctComplete = Math.round((completedSteps / setupSteps.length) * 100);

  return (
    <div style={{ maxWidth: 700 }}>
      {/* Header banner */}
      <div style={{
        background: allDone ? 'linear-gradient(135deg, #4FA038, #62BB46)' : 'linear-gradient(135deg, #2E8B98, #256F7A)',
        borderRadius: 14, padding: '24px 28px', marginBottom: 20, color: '#fff',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h3 style={{ fontSize: 18, fontWeight: 700, margin: '0 0 6px', color: '#fff' }}>
              {allDone ? 'CRM Fully Configured' : 'CRM Setup Wizard'}
            </h3>
            <p style={{ fontSize: 13, margin: 0, opacity: 0.9 }}>
              {allDone ? 'All 9 modules are seeded and operational.' : `${completedSteps} of ${setupSteps.length} modules configured — ${pctComplete}% complete`}
            </p>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 36, fontWeight: 800, lineHeight: 1 }}>{pctComplete}%</div>
            <div style={{ fontSize: 10, opacity: 0.8 }}>Complete</div>
          </div>
        </div>
        {/* Progress bar */}
        <div style={{ marginTop: 14, background: 'rgba(255,255,255,0.2)', borderRadius: 6, height: 8, overflow: 'hidden' }}>
          <div style={{ width: `${pctComplete}%`, height: '100%', background: '#fff', borderRadius: 6, transition: 'width 0.4s ease' }} />
        </div>
      </div>

      {/* Seed Everything button */}
      {!allDone && (
        <div style={{ marginBottom: 20, textAlign: 'center' }}>
          <button onClick={seedAll} disabled={seeding} style={{
            ...btnPrimary, background: 'linear-gradient(135deg, #256F7A, #2E8B98)', padding: '12px 32px', fontSize: 14,
            borderRadius: 10, boxShadow: '0 4px 14px rgba(99,102,241,0.3)',
          }}>
            <Zap size={16} /> {seeding ? 'Initializing All Modules...' : 'Initialize Everything'}
          </button>
          <p style={{ fontSize: 11, color: '#ADB5BD', marginTop: 6 }}>Seeds all unconfigured modules in one click</p>
        </div>
      )}

      {/* Step-by-step modules */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {setupSteps.map((step, i) => {
          const done = step.actual >= step.expected;
          const I = step.icon;
          return (
            <div key={step.key} style={{
              background: '#F8F9FA', borderRadius: 14, border: 'none', boxShadow: done ? `${neuShadowSm}, inset 0 0 0 2px #bbf7d0` : neuShadowSm,
              padding: '16px 20px', display: 'flex', gap: 14, alignItems: 'center',
              borderLeft: `4px solid ${done ? '#62BB46' : step.color}`, transition: 'all .2s',
            }}>
              {/* Step number */}
              <div style={{
                width: 32, height: 32, borderRadius: 16, flexShrink: 0,
                background: done ? '#62BB46' : `${step.color}18`, color: done ? '#fff' : step.color,
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700,
              }}>
                {done ? <CheckCircle size={16} /> : (i + 1)}
              </div>
              {/* Content */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#212529', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <I size={14} color={step.color} /> {step.label}
                  </span>
                  <span style={{ fontSize: 11, fontWeight: 600, color: done ? '#62BB46' : '#ADB5BD' }}>
                    {step.actual} / {step.expected}
                  </span>
                </div>
                <div style={{ fontSize: 11, color: '#6C757D', lineHeight: 1.4 }}>{step.desc}</div>
              </div>
              {/* Seed button */}
              {!done && canCreate ? (
                <button onClick={safeAsync(async () => { setSeeding(true); try { await step.seed(); } finally { setSeeding(false); } })}
                  disabled={seeding} style={{ ...btnSm, color: step.color, borderColor: `${step.color}40`, flexShrink: 0 }}>
                  <Zap size={11} /> Seed
                </button>
              ) : done ? <CheckCircle size={18} color="#62BB46" style={{ flexShrink: 0 }} /> : null}
            </div>
          );
        })}
      </div>

      {/* ═══ GoHighLevel Sync ═══ */}
      <GHLSyncSection canCreate={canCreate} loadExtended={loadExtended} />

      {/* ═══ Database Backup ═══ */}
      <BackupSection canCreate={canCreate} />

      {/* ═══ Meta / Facebook ═══ */}
      <MetaConfigSection canCreate={canCreate} />

      {/* Module Summary Grid */}
      <div style={{ marginTop: 20, background: '#F8F9FA', borderRadius: 14, border: 'none', boxShadow: neuShadow, padding: 20 }}>
        <h4 style={{ fontSize: 13, fontWeight: 700, color: '#212529', margin: '0 0 12px' }}>Live Status Dashboard</h4>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 8 }}>
          {setupSteps.map(s => {
            const done = s.actual >= s.expected;
            const I = s.icon;
            return (
              <div key={s.key} style={{ padding: 10, background: done ? '#f0fdf4' : '#f8fafc', borderRadius: 8, border: 'none', boxShadow: neuShadowSm, textAlign: 'center' }}>
                <I size={16} color={done ? '#62BB46' : '#ADB5BD'} style={{ marginBottom: 4 }} />
                <div style={{ fontSize: 18, fontWeight: 700, color: done ? '#62BB46' : '#212529' }}>{s.actual}</div>
                <div style={{ fontSize: 10, color: '#6C757D' }}>{s.label}</div>
              </div>
            );
          })}
          <div style={{ padding: 10, background: '#e0e5ec', borderRadius: 10, border: 'none', boxShadow: neuShadowInset, textAlign: 'center' }}>
            <Send size={16} color="#ADB5BD" style={{ marginBottom: 4 }} />
            <div style={{ fontSize: 18, fontWeight: 700, color: '#212529' }}>{templates.length}</div>
            <div style={{ fontSize: 10, color: '#6C757D' }}>Templates</div>
          </div>
          <div style={{ padding: 10, background: '#e0e5ec', borderRadius: 10, border: 'none', boxShadow: neuShadowInset, textAlign: 'center' }}>
            <Play size={16} color="#ADB5BD" style={{ marginBottom: 4 }} />
            <div style={{ fontSize: 18, fontWeight: 700, color: '#212529' }}>{workflowRuns.length}</div>
            <div style={{ fontSize: 10, color: '#6C757D' }}>Workflow Runs</div>
          </div>
        </div>
      </div>
    </div>
  );
}
